<template>
  <a
    v-if="href"
    :href="href"
    :class="classObject"
    :disabled="disabled"
    :target="blank ? '_blank' : ''"
    :rel="blank ? 'noopener' : ''"
    @click="clickA"
  >
    <span v-if="emlabel">{{ emlabel }}</span>
    <span v-if="labelInner" class="inner">{{ label }}</span>
    <template v-else>{{ label }}</template>
  </a>
  <button
    v-else
    type="button"
    :class="classObject"
    :disabled="disabled"
    @click="handleClick"
  >
    <span v-if="emlabel">{{ emlabel }}</span
    >{{ label }}
  </button>
</template>

<script lang="ts">
import { computed, defineComponent } from 'vue'
export default defineComponent({
  name: 'Button',

  props: {
    label: {
      type: String,
      default: 'ボタンラベル'
    },
    emlabel: {
      type: String,
      default: ''
    },
    labelInner: {
      type: Boolean,
      default: false
    },
    href: {
      type: String,
      require: false
    },
    blank: {
      type: Boolean,
      require: false
    },
    wide: {
      type: Boolean,
      default: false
    },
    wide303: {
      type: Boolean,
      default: false
    },
    primary: {
      type: Boolean,
      default: false
    },
    secondary: {
      type: Boolean,
      default: false
    },
    thingray: {
      type: Boolean,
      default: false
    },
    lightgray: {
      type: Boolean,
      default: false
    },
    border: {
      type: Boolean,
      default: false
    },
    favorite: {
      type: Boolean,
      default: false
    },
    line: {
      type: Boolean,
      default: false
    },
    facebook: {
      type: Boolean,
      default: false
    },
    google: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    sizeS: {
      type: Boolean,
      default: false
    },
    sizeLow: {
      type: Boolean,
      default: false
    },
    radiusS: {
      type: Boolean,
      default: false
    },
    zoom: {
      type: Boolean,
      default: false
    },

    handleClick: Function
  },

  setup(props) {
    const classObject = computed(() => {
      const {
        wide,
        primary,
        secondary,
        thingray,
        lightgray,
        border,
        favorite,
        line,
        facebook,
        google,
        disabled,
        sizeS,
        sizeLow,
        radiusS,
        zoom,
        wide303
      } = props
      return {
        button: true,
        wide,
        primary,
        secondary,
        thingray,
        lightgray,
        border,
        favorite,
        line,
        facebook,
        google,
        disabled,
        sizeS,
        sizeLow,
        radiusS,
        zoom,
        wide303
      }
    })

    const clickA = (e: any) => {
      if (props.blank) return true
      if (e.ctrlKey || e.metaKey) return true

      e.preventDefault()
      if (props.handleClick) return props.handleClick()
    }

    return {
      clickA,
      classObject
    }
  }
})
</script>

<style lang="scss" scoped>
@use 'src/styles/abstracts/mixins';
@use 'src/styles/abstracts/variables' as var;

.button {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  max-width: 215px;
  min-height: 50px;
  margin-left: auto;
  margin-right: auto;
  background-color: var.$color-white;
  border: 1px solid var.$color-border-gray;
  border-radius: 40px;
  box-shadow: 0 3px 6px rgba(0, 0, 0, 0.16);
  font-weight: bold;
  font-size: 1.4rem;
  color: var.$color-main-text;
  text-align: center;
  outline: none;
  padding-left: 20px;
  padding-right: 20px;
  line-height: 1.2;
  transition: 0.2s;
  cursor: pointer;

  @media screen and (max-width: 360px) {
    padding-right: 4px;
    padding-left: 4px;
    font-size: 1.3rem;
  }

  &.disabled,
  &:disabled {
    cursor: default;
    background-color: #d3d3d3;
    border-color: #d3d3d3;
    box-shadow: none;
    color: #fff;
    opacity: 1;
    cursor: not-allowed;

    &:hover {
      opacity: 1;
      background-color: #d3d3d3;
    }
    // pointer-events: none;
  }

  &:hover {
    opacity: 1;
    background-color: #f5f5f5;
  }

  &.sizeS {
    max-width: 150px;
    height: 40px;
    border-radius: 25px;
  }

  &.sizeLow {
    min-height: 40px;
  }

  &.radiusS {
    border-radius: 10px;
  }
}

.wide {
  height: 50px;
  line-height: 50px;
}

.primary {
  background-color: var.$color-main;
  border-color: var.$color-main;
  color: var.$color-white;

  span {
    color: #fffccb;
  }

  &:hover {
    background-color: #27b7af;
  }
}

.secondary {
  background-color: var.$color-primary;
  border-color: var.$color-primary;
  color: var.$color-white;

  &:hover {
    background-color: #d0366d;
  }
}

.primary.border {
  background-color: var.$color-white;
  border-color: var.$color-main;
  color: var.$color-main;

  &:hover {
    background-color: #e3f7f6;
  }
}

.secondary.border {
  background-color: var.$color-white;
  border-color: var.$color-primary;
  color: var.$color-primary;

  &:hover {
    background-color: #ffe9f0;
  }
}

.thingray {
  background-color: var.$color-thin-gray;
  color: #fff;
  border: none;

  &:hover {
    background-color: darken(var.$color-thin-gray, 10%);
  }
}

.lightgray {
  background-color: var.$color-secondary;
  border: none;

  &:hover {
    background-color: #b3eeea;
  }
}

.favorite {
  background-color: #f5f5f5;
  color: #666;
  font-weight: normal;

  &:hover {
    background-color: #e5f7f6;
  }
}

.line {
  max-width: none;
  margin-left: 0;
  margin-right: 0;
  background-color: #00c300;
  color: #fff;
  border-radius: 10px;
  position: relative;
  border: none;
  box-shadow: none;
  font-size: 1.7rem;
  padding-left: 72px;

  &:before {
    content: '';
    position: absolute;
    left: 7px;
    top: 8px;
    background: url(~@/assets/images/common/line-logo.svg);
    display: block;
    width: 36px;
    height: 34px;
  }

  &:after {
    content: '';
    display: block;
    position: absolute;
    top: 0;
    left: 50px;
    width: 2px;
    height: 50px;
    background-color: #00b300;
    transition: background-color 0.2s;
  }

  &:hover {
    background-color: #00e000;

    &:after {
      background-color: #00c900;
    }
  }

  &:active {
    background-color: #00b300;

    &:after {
      background-color: #009800;
    }
  }
}

.zoom {
  max-width: 300px;
  .inner {
    position: relative;
    padding-right: 30px;
    &:after {
      content: '';
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      right: 0;
      background: url(~@/assets/images/icon/zoom.svg);
      display: block;
      width: 19px;
      height: 11px;
    }
  }
}

.wide303 {
  max-width: 303px;
}

// .facebook {
//   max-width: none;
//   margin-left: 0;
//   margin-right: 0;
//   background-color: #3b5998;
//   color: #fff;
// }

// .google {
//   max-width: none;
//   margin-left: 0;
//   margin-right: 0;
//   background-color: #ea4335;
//   color: #fff;
// }
</style>
